import { Dialog } from '@/features/dialog/dialog';
import { DialogStateManager, useDialog } from '@/features/dialog/dialog-provider';
import type { SelectedLineItemProduct } from '@/features/sales-draft/types/normal-line-machine-types';
import { useAppState } from '@/lib/hooks/use-app-state';
import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';
import { Catalog } from './catalog';

interface CatalogDialogProps {
    baseUrl: string;
    lastUrl: string;
    setLastUrl: (url: string) => void;
    onAddLineItem: (product: SelectedLineItemProduct, quantity: number) => void;
}

interface CatalogDialogResult {
    confirmed?: boolean;
}

// The actual dialog component with iframe
const CatalogDialog = DialogStateManager.create(
    ({ baseUrl, lastUrl, onAddLineItem, setLastUrl }: CatalogDialogProps) => {
        const { visible, hide } = useDialog();

        return (
            <Dialog showClose open={visible} onClose={hide}>
                <Dialog.Content className="h-[calc(100vh-20px)] w-[calc(100vw-20px)] max-w-full overflow-hidden rounded-lg p-0">
                    <Catalog
                        baseUrl={baseUrl}
                        lastUrl={lastUrl}
                        setLastUrl={setLastUrl}
                        onAddLineItem={onAddLineItem}
                    />
                </Dialog.Content>
            </Dialog>
        );
    },
);

interface CatalogContextType {
    showCatalog: (
        onAddLineItem: (product: SelectedLineItemProduct, quantity: number) => void,
        draftId?: string,
    ) => Promise<CatalogDialogResult>;
}

const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

export function CatalogProvider({ children }: { children: ReactNode }) {
    const { platoUser } = useAppState();
    const webshopUrl = platoUser?.platoCustomer.webshopUrl || '';
    const [lastUrl, setLastUrl] = useState<string | null>(null);
    const [lastDraftId, setLastDraftId] = useState<string | undefined>();

    const showCatalog = async (
        onAddLineItem: (product: SelectedLineItemProduct, quantity: number) => void,
        draftId?: string,
    ) => {
        setLastDraftId(draftId);
        // @ts-ignore  -- TODO: Jonmer show oliver how to fix this
        return DialogStateManager.show<CatalogDialogResult, CatalogDialogProps>(CatalogDialog, {
            baseUrl: webshopUrl,
            lastUrl: lastUrl || webshopUrl,
            setLastUrl,
            onAddLineItem,
        });
    };

    return <CatalogContext.Provider value={{ showCatalog }}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
    const context = useContext(CatalogContext);
    if (!context) {
        throw new Error('useCatalog must be used within a CatalogProvider');
    }
    return context;
}
